import * as THREE from 'three';
import type { FoodField } from '../sim/contracts';
import type { Terrain } from '../game/Terrain';

const CLUSTER_RADIUS = 0.06;
const SPHERE_RADIUS = 0.006;   // 6 mm — the ant-pickable grain
const SURFACE_LIFT = 0.002;

export class FoodMarkers {
  readonly group = new THREE.Group();
  private sugarMesh: THREE.InstancedMesh;
  private corpseMesh: THREE.InstancedMesh;
  private readonly sugarCapacity: number;
  private readonly dummy = new THREE.Object3D();

  constructor(
    private foodField: FoodField,
    private terrain: Terrain,
    private maxPiles: number,
    opts: { sugarColor?: number; leafColor?: number } = {},
  ) {
    this.group.name = 'food-markers';

    void opts.leafColor; // leaves retired — pile = individual spheres the ants grab.

    // Reserve enough instance slots for a worst-case pile-size (300 units per
    // pile × N piles). Only actually-alive units are drawn each frame.
    this.sugarCapacity = maxPiles * 300;

    const sugarGeo = new THREE.SphereGeometry(SPHERE_RADIUS, 12, 8);
    const sugarMat = new THREE.MeshStandardMaterial({
      color: opts.sugarColor ?? 0xffffff,
      roughness: 0.45,
      metalness: 0.0,
    });
    this.sugarMesh = new THREE.InstancedMesh(sugarGeo, sugarMat, this.sugarCapacity);
    this.sugarMesh.castShadow = false;
    this.sugarMesh.receiveShadow = true;
    this.sugarMesh.count = 0;
    this.sugarMesh.frustumCulled = false;

    // Corpse food is a tiny flat disc on the ground.
    const corpseGeo = new THREE.CircleGeometry(0.015, 12);
    corpseGeo.rotateX(-Math.PI / 2);
    const corpseMat = new THREE.MeshStandardMaterial({
      color: 0x3a2510,
      roughness: 0.8,
      side: THREE.DoubleSide,
    });
    this.corpseMesh = new THREE.InstancedMesh(corpseGeo, corpseMat, maxPiles * 300);
    this.corpseMesh.castShadow = false;
    this.corpseMesh.receiveShadow = true;
    this.corpseMesh.count = 0;
    this.corpseMesh.frustumCulled = false;

    this.group.add(this.sugarMesh);
    this.group.add(this.corpseMesh);
  }

  syncInstances(): void {
    const items = this.foodField.items;
    const n = Math.min(items.length, this.maxPiles);

    let sugarIdx = 0;
    let corpseIdx = 0;

    for (let p = 0; p < n; p++) {
      const it = items[p];
      const remaining = Math.max(0, Math.floor(it.amount));
      if (remaining <= 0) continue;

      const rng = seededRng(it.id * 9173 + 1);

      if (it.kind === 'pile') {
        for (let c = 0; c < remaining; c++) {
          if (sugarIdx >= this.sugarCapacity) break;
          const a = rng() * Math.PI * 2;
          const r = Math.sqrt(rng()) * CLUSTER_RADIUS;
          const cx = it.x + Math.cos(a) * r;
          const cz = it.z + Math.sin(a) * r;
          // Stack spheres slightly so a pile actually looks piled.
          const heightPhase = rng();
          const cy = this.terrain.getHeight(cx, cz) + SURFACE_LIFT + SPHERE_RADIUS + heightPhase * SPHERE_RADIUS;
          this.dummy.position.set(cx, cy, cz);
          this.dummy.rotation.set(0, rng() * Math.PI * 2, 0);
          this.dummy.scale.setScalar(0.85 + rng() * 0.35);
          this.dummy.updateMatrix();
          this.sugarMesh.setMatrixAt(sugarIdx++, this.dummy.matrix);
        }
      } else if (it.kind === 'corpse') {
        if (corpseIdx >= this.corpseMesh.count + this.corpseMesh.instanceMatrix.count) break;
        const cx = it.x;
        const cz = it.z;
        const cy = this.terrain.getHeight(cx, cz) + SURFACE_LIFT;
        this.dummy.position.set(cx, cy, cz);
        this.dummy.rotation.set(0, rng() * Math.PI * 2, 0);
        this.dummy.scale.setScalar(1);
        this.dummy.updateMatrix();
        this.corpseMesh.setMatrixAt(corpseIdx++, this.dummy.matrix);
      }
    }

    this.sugarMesh.count = sugarIdx;
    this.corpseMesh.count = corpseIdx;
    this.sugarMesh.instanceMatrix.needsUpdate = true;
    this.corpseMesh.instanceMatrix.needsUpdate = true;
  }
}

function seededRng(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
